import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, UNSAFE_NavigationContext } from "react-router-dom";
import { toast } from "sonner";
import { useDemographicsForm, type ProfileInfoMode } from "@/hooks/useDemographicsForm";
import { useWizardInputScrollIntoView } from "@/hooks/useWizardInputScrollIntoView";
import { Button } from "@/components/ui/Button";
import { WizardActionsBar } from "@/components/wizard/WizardActionsBar";
import { StepLoadError } from "@/components/complaints/ComplaintStepHeader";
import { DemographicsPersonalSection } from "./DemographicsPersonalSection";
import { DemographicsEmergencySection } from "./DemographicsEmergencySection";

export interface ProfileEditActions {
  /** False while the info is loading or failed to load; the parent hides Edit until there is a form to edit. */
  ready: boolean;
  editing: boolean;
  hasDirty: boolean;
  saving: boolean;
  startEdit: () => void;
  cancel: () => void;
  save: () => Promise<void>;
}

export interface DemographicsFormProps {
  mode?: ProfileInfoMode;
  onBack?: () => void;
  onSaveAndContinue?: () => void;
  /** Profile mode only: call with actions so parent can render Edit/Update in tab bar. */
  onEditActions?: (actions: ProfileEditActions) => void;
}

export interface DemographicsFormHandle {
  isDirty: () => boolean;
  isEditing: () => boolean;
}

const DemographicsFormInner = forwardRef<DemographicsFormHandle, DemographicsFormProps>(function DemographicsFormInner(
  {
    mode = "profile",
    onBack,
    onSaveAndContinue,
    onEditActions,
  },
  ref
) {
  const navigate = useNavigate();
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const onValidationErrorRef = useRef<() => void>(() => {});
  const {
    patient,
    loading,
    loadError,
    reload,
    errors,
    saving,
    update,
    save,
    revertFields,
    handleSubmit,
    isWizard,
    requiredComplete,
    SEX_OPTIONS,
    stateOptions,
    maritalOptions,
    contactPrefOptions,
    employmentOptions,
    emerRelationOpts,
  } = useDemographicsForm({
    mode,
    onSaveAndContinue,
    onValidationError: () => onValidationErrorRef.current(),
  });

  const [personalDirty, setPersonalDirty] = useState(false);
  const [emergencyDirty, setEmergencyDirty] = useState(false);
  const hasDirty = personalDirty || emergencyDirty;

  const [profileEditing, setProfileEditing] = useState(false);
  const [profileEditSaving, setProfileEditSaving] = useState(false);
  const fullSnapshotRef = useRef<Record<string, string>>({});
  const initialLoadDoneRef = useRef(false);

  useImperativeHandle(ref, () => ({
    isDirty: () => personalDirty || emergencyDirty,
    isEditing: () => profileEditing,
  }), [personalDirty, emergencyDirty, profileEditing]);

  const isProfileMode = !isWizard;
  const startEdit = useCallback(() => {
    fullSnapshotRef.current = { ...patient };
    setProfileEditing(true);
  }, [patient]);
  const cancelEdit = useCallback(() => {
    revertFields(fullSnapshotRef.current);
    setProfileEditing(false);
  }, [revertFields]);
  const saveEdit = useCallback(async () => {
    setProfileEditSaving(true);
    try {
      const ok = await save();
      if (ok) setProfileEditing(false);
    } catch (e) {
      // save() throws when the API rejects the update; without this the click looks dead (WEB-1857).
      console.error("Demographics save failed:", e);
      toast.error("We couldn't save your information. Please check it and try again.");
    } finally {
      setProfileEditSaving(false);
    }
  }, [save]);

  const startEditRef = useRef(startEdit);
  const cancelEditRef = useRef(cancelEdit);
  const saveEditRef = useRef(saveEdit);
  startEditRef.current = startEdit;
  cancelEditRef.current = cancelEdit;
  saveEditRef.current = saveEdit;
  onValidationErrorRef.current = () => setProfileEditing(true);

  useLayoutEffect(() => {
    if (isProfileMode && onEditActions) {
      onEditActions({
        ready: !loading && !loadError,
        editing: profileEditing,
        hasDirty,
        saving: profileEditSaving,
        startEdit: () => startEditRef.current(),
        cancel: () => cancelEditRef.current(),
        save: () => saveEditRef.current(),
      });
    }
  }, [isProfileMode, onEditActions, loading, loadError, profileEditing, hasDirty, profileEditSaving]);

  // When wizard first loads: if required fields are incomplete, open in edit mode and force completion
  useEffect(() => {
    if (!isWizard || loading || loadError) return;
    if (initialLoadDoneRef.current) return;
    initialLoadDoneRef.current = true;
    if (!requiredComplete) {
      setProfileEditing(true);
    }
  }, [isWizard, loading, loadError, requiredComplete]);

  /** When entering edit mode, move real focus to first field so iOS shows caret/keyboard without an extra tap. */
  const prevProfileEditingRef = useRef(false);
  useLayoutEffect(() => {
    if (!profileEditing) {
      prevProfileEditingRef.current = false;
      return;
    }
    if (prevProfileEditingRef.current) return;
    prevProfileEditingRef.current = true;
    const focusFirst = () => {
      const el = document.getElementById("demographics-firstName");
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
        el.focus();
        void el.offsetHeight;
      }
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(focusFirst);
    });
  }, [profileEditing]);

  const blockRef = useRef(false);
  blockRef.current = hasDirty;

  const [pendingNav, setPendingNav] = useState<(() => void) | null>(null);
  const [showWizardDiscardModal, setShowWizardDiscardModal] = useState(false);
  const wizardScrollRef = useRef<HTMLDivElement>(null);
  useWizardInputScrollIntoView(wizardScrollRef, isWizard && !loading);

  useEffect(() => {
    type NavWithPushReplace = {
      push: (to: unknown, state?: unknown) => void;
      replace: (to: unknown, state?: unknown) => void;
    };
    const nav = navigator as NavWithPushReplace;
    const origPush = nav.push.bind(nav);
    const origReplace = nav.replace.bind(nav);

    nav.push = (to: unknown, state?: unknown) => {
      if (blockRef.current) {
        setPendingNav(() => () => origPush(to, state));
      } else {
        origPush(to, state);
      }
    };

    nav.replace = (to: unknown, state?: unknown) => {
      if (blockRef.current) {
        setPendingNav(() => () => origReplace(to, state));
      } else {
        origReplace(to, state);
      }
    };

    return () => {
      nav.push = origPush;
      nav.replace = origReplace;
    };
  }, [navigator]);

  useEffect(() => {
    if (!hasDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasDirty]);

  const handleStay = useCallback(() => setPendingNav(null), []);
  const handleLeave = useCallback(() => {
    blockRef.current = false;
    pendingNav?.();
    setPendingNav(null);
  }, [pendingNav]);

  if (loading) {
    return (
      <div className="flex w-full flex-col items-center justify-center p-6">
        <div className="h-1 w-full max-w-xs overflow-hidden rounded bg-primary/20">
          <div className="h-full w-1/3 animate-pulse rounded bg-primary" />
        </div>
        <p className="my-24 mt-3 text-lg font-semibold text-primary">
          Loading personal information...
        </p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={isWizard ? "flex min-h-0 w-full min-w-0 flex-1 flex-col" : undefined}>
        <div className={isWizard ? "wizard-page__content py-2" : "py-2"}>
          <StepLoadError message="We couldn't load your information." onRetry={reload} />
        </div>
        {isWizard && (
          <WizardActionsBar>
            <Button
              type="button"
              variant="outline"
              onClick={() => (onBack ? onBack() : navigate("/"))}
              aria-label="Back"
            >
              Back
            </Button>
          </WizardActionsBar>
        )}
      </div>
    );
  }

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className={
          isWizard
            ? "flex min-h-0 w-full min-w-0 flex-1 flex-col"
            : ""
        }
      >
        <div
          ref={isWizard ? wizardScrollRef : undefined}
          className={
            isWizard
              ? `wizard-page__content wizard-demographics-wizard-scroll min-h-0 min-w-0 py-2 px-0${profileEditing ? " wizard-demographics__scroll--editing" : ""}`
              : "space-y-2"
          }
        >
          {isWizard ? (
            <div className="wizard-demographics-wizard-scroll-inner flex min-h-0 flex-col gap-2 pb-3">
              <DemographicsPersonalSection
                patient={patient}
                update={update}
                errors={errors}
                onSave={save}
                revertFields={revertFields}
                onDirtyChange={setPersonalDirty}
                editingOverride={profileEditing}
                hideHeaderActions={mode === "profile" || isWizard}
                sexOptions={SEX_OPTIONS}
                stateOptions={stateOptions}
                maritalOptions={maritalOptions}
                contactPrefOptions={contactPrefOptions}
                employmentOptions={employmentOptions}
                singleColumn={isWizard}
              />
              <DemographicsEmergencySection
                patient={patient}
                update={update}
                errors={errors}
                onSave={save}
                revertFields={revertFields}
                onDirtyChange={setEmergencyDirty}
                editingOverride={profileEditing}
                hideHeaderActions={mode === "profile" || isWizard}
                emerRelationOpts={emerRelationOpts}
                singleColumn={isWizard}
              />
            </div>
          ) : (
            <>
              <DemographicsPersonalSection
                patient={patient}
                update={update}
                errors={errors}
                onSave={save}
                revertFields={revertFields}
                onDirtyChange={setPersonalDirty}
                editingOverride={profileEditing}
                hideHeaderActions={mode === "profile" || isWizard}
                sexOptions={SEX_OPTIONS}
                stateOptions={stateOptions}
                maritalOptions={maritalOptions}
                contactPrefOptions={contactPrefOptions}
                employmentOptions={employmentOptions}
                singleColumn={isWizard}
              />
              <DemographicsEmergencySection
                patient={patient}
                update={update}
                errors={errors}
                onSave={save}
                revertFields={revertFields}
                onDirtyChange={setEmergencyDirty}
                editingOverride={profileEditing}
                hideHeaderActions={mode === "profile" || isWizard}
                emerRelationOpts={emerRelationOpts}
                singleColumn={isWizard}
              />
            </>
          )}
        </div>
        {isWizard ? (
          <WizardActionsBar>
            <Button
              type="button"
              variant="outline"
              onClick={() => (onBack ? onBack() : navigate("/"))}
              aria-label="Back"
            >
              Back
            </Button>
            {!profileEditing ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={startEdit}
                  className="flex min-h-11 items-center gap-1.5 px-3 text-sm font-semibold text-healthsource-navy transition-colors hover:text-healthsource-red"
                  aria-label="Edit"
                >
                  <i className="fas fa-pen" />
                  Edit
                </button>
                <Button type="submit" disabled={saving}>
                  {saving ? "Saving…" : "Continue"}
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => (hasDirty ? setShowWizardDiscardModal(true) : cancelEdit())}
                  className="min-h-11 px-3 text-sm font-semibold text-healthsource-gray underline transition-colors hover:text-healthsource-red"
                >
                  Cancel
                </button>
                <Button type="button" onClick={saveEdit} disabled={profileEditSaving}>
                  {profileEditSaving ? "Saving…" : "Save"}
                </Button>
              </div>
            )}
          </WizardActionsBar>
        ) : null}
      </form>

      {pendingNav && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <div className="max-h-[90dvh] w-full max-w-sm overflow-y-auto rounded-xl bg-white p-6 shadow-lg" style={{ boxShadow: "0 8px 32px rgba(30,37,43,0.18)" }}>
            <h3 className="text-[15px] font-extrabold text-healthsource-dark">Unsaved Changes</h3>
            <p className="mt-2 text-[13px] text-healthsource-gray">
              You have unsaved changes that will be lost if you leave this page.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={handleStay}
                className="min-h-11 rounded-md border border-healthsource-lightLavender px-4 text-[13px] font-bold text-healthsource-dark transition-colors hover:bg-healthsource-lightGray"
              >
                Stay
              </button>
              <button
                type="button"
                onClick={handleLeave}
                className="min-h-11 rounded-md px-4 text-[13px] font-bold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: "var(--hs-red)" }}
              >
                Leave Page
              </button>
            </div>
          </div>
        </div>
      )}

      {showWizardDiscardModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <div className="max-h-[90dvh] w-full max-w-sm overflow-y-auto rounded-xl bg-white p-6 shadow-lg" style={{ boxShadow: "0 8px 32px rgba(30,37,43,0.18)" }}>
            <h3 className="text-[15px] font-extrabold text-healthsource-dark">Discard changes?</h3>
            <p className="mt-2 text-[13px] text-healthsource-gray">
              You have unsaved changes. Are you sure you want to discard them?
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowWizardDiscardModal(false)}
                className="min-h-11 rounded-md border border-healthsource-lightLavender px-4 text-[13px] font-bold text-healthsource-dark transition-colors hover:bg-healthsource-lightGray"
              >
                Keep Editing
              </button>
              <button
                type="button"
                onClick={() => {
                  cancelEdit();
                  setShowWizardDiscardModal(false);
                }}
                className="min-h-11 rounded-md px-4 text-[13px] font-bold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: "var(--hs-red)" }}
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export const DemographicsForm = DemographicsFormInner;
